import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';

/**
 * Normalizes a phone number to E.164 format
 * @param {string} phone - Phone number to normalize
 * @param {string} defaultCountry - Default country code (default: 'BE')
 * @returns {string|null} Normalized phone number or null if invalid
 */
export const normalizePhoneNumber = (phone, defaultCountry = 'BE') => {
    if (!phone || typeof phone !== 'string') return null;

    // Remove common formatting characters
    const cleaned = phone.replace(/[\s\-\(\)\/\.]/g, '');

    // If already in E.164 format, return as is
    if (cleaned.startsWith('+')) {
        try {
            const parsed = parsePhoneNumber(cleaned);
            return parsed ? parsed.number : null;
        } catch (e) {
            return null;
        }
    }

    // Try to parse with default country
    try {
        const withCountry = cleaned.startsWith('0')
            ? `+32${cleaned.substring(1)}` // Belgian number starting with 0
            : `+${cleaned}`; // Already has country code without +

        const parsed = parsePhoneNumber(withCountry);
        return parsed ? parsed.number : null;
    } catch (e) {
        return null;
    }
};

/**
 * Validates an email address format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid
 */
export const isValidEmail = (email) => {
    // Check for standard email format AND valid TLD (at least 2 letters)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
};



/**
 * Validates a phone number using react-phone-number-input
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid
 */
export const isValidPhone = (phone) => {
    if (!phone) return true; // Phone is optional
    return isValidPhoneNumber(phone);
};

/**
 * Validates a website URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid
 */
export const isValidUrl = (url) => {
    if (!url) return true; // Website is optional
    try {
        // Check if URL starts with http:// or https://
        // AND has a valid TLD (at least 2 letters at the end)
        const urlRegex = /^https?:\/\/.+\.[a-z]{2,}$/i;
        if (!urlRegex.test(url)) {
            return false;
        }
        // Try to create a URL object to validate structure
        new URL(url);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Checks if a value is not empty
 * @param {string} value - Value to check
 * @returns {boolean} True if not empty
 */
export const isRequired = (value) => {
    return value && value.trim().length > 0;
};

/**
 * Validates form data and returns errors
 * @param {Object} formData - Form data to validate
 * @param {Object} translations - Translation object for error messages
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const validateForm = (formData, translations) => {
    const errors = {};

    if (!isRequired(formData.firstName)) {
        errors.firstName = translations.validation.required;
    }

    if (!isRequired(formData.lastName)) {
        errors.lastName = translations.validation.required;
    }

    if (!isRequired(formData.email)) {
        errors.email = translations.validation.required;
    } else if (!isValidEmail(formData.email)) {
        errors.email = translations.validation.email;
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
        errors.phone = translations.validation.phone;
    }

    if (formData.website && !isValidUrl(formData.website)) {
        errors.website = translations.validation.website ||
            (translations.validation.fr ? 'Format d\'URL invalide (doit commencer par http:// ou https://)' : 'Invalid URL format (must start with http:// or https://)');
    }

    return errors;
};
